import apiClient from "./client";

import { createConversationClient } from "@/api/agent-conversation";
import type { ActivityTablesSummary, ScopeVerdictResult } from "@/api/configuration-server";
import type { ScopeVerdict } from "@/lib/org-structure-tree";

/**
 * Cliente do Nó 04 do agente de configuração: as configurações por módulo
 * (`configuracoes_fi` hoje), cada uma SSCUI ou app Fiori (spec 2026-10-12, §8).
 *
 * A lista e a decisão de escopo são rotas próprias do Nó 04: as do Nó 03 fixam o
 * domínio `outras_configuracoes` de propósito. Os registros do de-para usam a rota
 * comum `/tables/{t}/records` com `activity_code`.
 */

export interface ModuleConfigActivitiesQuery {
  /** Domínio do módulo da aba (hoje só `configuracoes_fi`). */
  domain: string;
  artifact_id?: string;
}

const basePath = (projectId: string) =>
  `/api/projects/${projectId}/configuration-server/module-config`;

function moduleParams(query: ModuleConfigActivitiesQuery): Record<string, string> {
  return query.artifact_id
    ? { domain: query.domain, artifact_id: query.artifact_id }
    : { domain: query.domain };
}

export async function listModuleConfigActivityTables(
  projectId: string,
  activityCode: string,
  query: ModuleConfigActivitiesQuery,
): Promise<ActivityTablesSummary> {
  const response = await apiClient.get<ActivityTablesSummary>(
    `${basePath(projectId)}/activities/${encodeURIComponent(activityCode)}/tables`,
    { params: moduleParams(query) },
  );
  return response.data;
}

/**
 * Aprova ou tira do escopo uma configuração do Nó 04. Tirar do escopo uma
 * configuração que já teve conversa volta 409 `item_already_executed`.
 */
export async function setModuleConfigScopeVerdict(
  projectId: string,
  activityCode: string,
  verdict: Exclude<ScopeVerdict, "pending">,
  artifactId?: string,
): Promise<ScopeVerdictResult> {
  const response = await apiClient.post<ScopeVerdictResult>(
    `${basePath(projectId)}/scope-verdict`,
    artifactId
      ? { activity_code: activityCode, verdict, artifact_id: artifactId }
      : { activity_code: activityCode, verdict },
  );
  return response.data;
}

/** A conversa do agente com uma configuração do Nó 04. */
export const moduleConfigConversation = createConversationClient("module-config");
